import React from 'react';

import { Panel } from '../kit.js';
import { ACCESS_GROUPS, useBuilding } from '../../state/building.js';
import { Icon } from './glyphs.js';
import { Switch } from './parts.js';
import { hoursText } from './model.js';

/* Schedules and access groups: when each amenity's doors honour a
   resident's fob, and which doors each group opens. Switching one here
   rewrites the hours on the door cards above, and closes or reopens the
   matching tile on the resident's Door Access screen. */
export function Schedules({ notify, index = 0 }) {
    const { schedules, holidayHours, setHolidayHours, patch, logAccess, doors, credentials } = useBuilding();

    const toggle = (schedule, enabled) => {
        patch('schedules', schedule.id, { enabled });
        logAccess({
            kind: 'system', tone: enabled ? 'ok' : 'warn', label: 'Schedule',
            title: `${schedule.name} schedule ${enabled ? 'on' : 'off'}`, who: 'Front desk', via: 'Schedules'
        });
        notify(schedule.kind === 'unlock'
            ? (enabled ? `${schedule.name} resumes, ${hoursText(schedule, holidayHours)}.` : `${schedule.name} paused. The doors need a credential all day.`)
            : (enabled ? `${schedule.name} is open to ${schedule.who.toLowerCase()} again, ${hoursText(schedule, holidayHours)}.` : `${schedule.name} is closed to ${schedule.who.toLowerCase()} until you switch it back on.`));
    };

    const holiday = on => {
        setHolidayHours(on);
        notify(on ? 'Holiday hours are in effect. Door cards and resident screens show the holiday times.' : 'Regular hours are back in effect.');
    };

    const doorNames = ids => ids.map(id => doors.find(d => d.id === id)?.name).filter(Boolean).join(', ');

    return (
        <div className="split acc-sched">
            <Panel index={index} className="acc-sched__panel">
                <div className="acc-holiday">
                    <span className="acc-holiday__icon"><Icon name="clock" size={20} /></span>
                    <div>
                        <h4>Holiday hours</h4>
                        <p>Shorter amenity hours and later free passage, for holidays and building events.</p>
                    </div>
                    <Switch checked={holidayHours} onChange={holiday} label="Holiday hours" />
                </div>

                <ul className="acc-sched__list">
                    {schedules.map(s => (
                        <li className={`acc-sched__row${s.enabled ? '' : ' acc-sched__row--off'}`} key={s.id}>
                            <div className="acc-sched__main">
                                <span className="acc-sched__name">{s.name}</span>
                                <span className="acc-sched__hours">
                                    <span className="acc-num">{s.enabled ? `${s.who}, ${hoursText(s, holidayHours)}` : (s.kind === 'unlock' ? 'Paused, credential required' : 'Closed')}</span>
                                    {s.enabled && holidayHours && <span className="acc-holiday-tag">Holiday</span>}
                                </span>
                                {doorNames(s.doors) !== s.name && <span className="acc-sched__doors">{doorNames(s.doors)}</span>}
                            </div>
                            <Switch checked={s.enabled} onChange={on => toggle(s, on)} label={`${s.name} schedule`} />
                        </li>
                    ))}
                </ul>
            </Panel>

            <div className="stack">
                <h4>Access groups</h4>
                <div className="acc-groups">
                    {ACCESS_GROUPS.map((group, i) => {
                        const groupDoors = doors.filter(d => d.group === group);
                        const holders = credentials.filter(c => c.status === 'Active' && c.groups.includes(group)).length;
                        return (
                            <Panel index={index + i} className="acc-group" key={group}>
                                <div className="acc-group__head">
                                    <span className="acc-group__name">{group}</span>
                                    <span className="acc-group__count acc-num">{holders}</span>
                                </div>
                                <span className="acc-group__meta">{groupDoors.length} {groupDoors.length === 1 ? 'door' : 'doors'} · {holders} active credentials</span>
                                <span className="acc-group__doors">{groupDoors.map(d => d.name).join(', ')}</span>
                            </Panel>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
